import React, { useEffect } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { Button, PageHeader, Select, Spin } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'

import { mimeTypes } from 'utils/file'
import { priorities, statusOptions } from 'utils/constants'
import Uploader from 'components/uploadFile'
import useDemandDetails from 'pages/detailsDemands/hooks/useDemandDetails'
import FormSection from 'pages/demandsCreate/components/FormSection'
import InputForm from 'pages/demandsCreate/components/inputForm'
import TextAreaForm from 'pages/demandsCreate/components/textAreaForm'
import { errorMessage, successMessage } from 'services/messager'
import {
	getOptions,
	updateDemand,
	getSolicitantAreasOptions,
} from 'services/demands/demandsServices'
import useDemandAttachments from 'hooks/demands/useDemandAttachments'

import { StyledForm } from './styles'
import { StyledFormItem } from 'pages/demandsCreate/styles'
import styles from './index.module.scss'
import { useSelector } from 'react-redux'

const EditDemand = () => {
	const history = useHistory()
	const queryClient = useQueryClient()
	const { id } = useParams()
	const { data, isLoading, isError } = useDemandDetails(id)
	const user = useSelector(({ session }) => session)
	const requireTemplate = !isLoading && data?.category?.require_template

	// Checks if user is from company AllCare to change label and enable edit task name
	const taskNameLabel =
		user.company_id === 96 ? 'Título do chamado' : 'Fornecedor/Contraparte'
	const isTaskNameEditable = user.company_id === 96

	const {
		data: attachments,
		isLoading: isLoadingAttachments,
		isError: isErrorAttachments,
	} = useDemandAttachments(id)

	const { control, handleSubmit, watch, setValue, reset } = useForm({
		defaultValues: {
			taskName: '',
			description: '',
			categoryId: undefined,
			templateId: undefined,
			priority: undefined,
			status: undefined,
			fileList: [],
			removedFiles: [],
			solicitantAreaId: undefined,
		},
	})

	const fileList = watch('fileList')
	const removedFiles = watch('removedFiles') ?? []

	const { data: solicitantAreas, isLoading: isLoadingAreas } = useQuery({
		queryKey: ['solicitant-areas-options'],
		queryFn: () => getSolicitantAreasOptions(''),
		staleTime: 1000 * 30,
		refetchOnWindowFocus: false,
		retry: false,
	})

	useEffect(() => {
		if (!isLoading) {
			reset({
				taskName: data.name,
				description: data.description,
				categoryId: data.category.id,
				templateId: data.document_template_id,
				priority: data.priority,
				status: data.status,
				solicitantAreaId: data.solicitantArea?.id,
			})
		}
	}, [data, isLoading, reset])

	useEffect(() => {
		if (!isLoadingAttachments && attachments.length > 0) {
			setValue(
				'fileList',
				attachments?.map((attach) => ({
					id: attach.id,
					uid: attach.id,
					name: attach.original_name,
				}))
			)
		}
	}, [attachments, isLoadingAttachments, setValue])

	const { data: categories, isLoading: isLoadingCategories } = useQuery({
		queryKey: ['category-options'],
		queryFn: fetchCategories,
		staleTime: 1000 * 30,
		refetchOnWindowFocus: false,
		retry: false,
	})

	const { data: models, isLoading: isLoadingModels } = useQuery({
		queryKey: ['models-options'],
		queryFn: fetchModels,
		staleTime: 1000 * 30,
		refetchOnWindowFocus: false,
		retry: false,
	})

	async function fetchCategories() {
		try {
			const data = await getOptions({
				route: '/categories/list',
				page: 1,
				limit: 100,
			})

			return data.list
		} catch (error) {
			console.log(error)
			errorMessage({
				content: 'Erro ao obter categorias',
			})
		}
	}

	async function fetchModels() {
		try {
			const data = await getOptions({
				route: '/templates/list',
				page: 1,
				limit: 100,
			})

			return data.list
		} catch (error) {
			console.log(error)
			errorMessage({
				content: 'Erro ao obter modelos',
			})
		}
	}

	const { mutate: editDemand, isPending: isLoadingEdit } = useMutation({
		mutationFn: async (values) => {
			const formValues = { ...values, taskId: id }
			const formData = new FormData()

			Object.entries(formValues).forEach(([key, value]) => {
				if (
					Array.isArray(value) &&
					value.some((file) => file instanceof File)
				) {
					value.forEach((file) => {
						if (file instanceof File) {
							formData.append('files', file)
						}
					})
				}

				if (key === 'removedFiles') {
					formData.append(key, JSON.stringify(value))
					return
				}

				if (value !== undefined && value !== null) {
					formData.append(key, value)
				}
			})

			const data = await updateDemand(formData)

			return data
		},
		onSuccess: () => {
			successMessage({
				content: 'Demanda editada com sucesso!',
			})
			queryClient.invalidateQueries({ queryKey: ['demands'] })
			queryClient.invalidateQueries({ queryKey: ['demand', id] })
			queryClient.invalidateQueries({ queryKey: ['demand-attachments', id] })
			history.push(`/demands/${id}`)
		},
		onError: (error) => {
			console.log(error)
			errorMessage({
				content: 'Erro ao editar demanda.',
			})
		},
	})

	// const { mutate: removeAttachment, isPending: isLoadingRemoveAttach } =
	// 	useMutation({
	// 		mutationFn: async (file) => {
	// 			if (file instanceof File) return

	// 			// TODO: implement remove file request when API is ready

	// 		},
	// 		onError: (error) => {
	// 			console.log(error)
	// 			errorMessage({
	// 				content: 'Erro ao remover arquivo da demanda',
	// 			})
	// 		},
	// 	})

	function handleRemoveAttachment(currentFile) {
		if (currentFile instanceof File) return

		const file = attachments.find((attach) => attach.id === currentFile.id)
		setValue('removedFiles', [...removedFiles, file])
	}

	async function onSubmit(values) {
		editDemand(values)
	}

	const getSelectOptions = (options) => {
		if (!options) return <></>

		return (
			<>
				{options.map((option) => {
					const value = option.id || option.value
					const label = option.name || option.label

					return (
						<Select.Option key={value} value={value}>
							{label}
						</Select.Option>
					)
				})}
			</>
		)
	}

	if (isError || isErrorAttachments) {
		return (
			<div className={styles.layout}>
				<PageHeader
					title="Editar informações da demanda"
					onBack={() => history.push(`/demands/${id}`)}
					className={styles.header}
				/>
				<div className={styles.content}>
					<p>
						Erro ao obter informações da demanda. Tente novamente mais tarde.
					</p>
				</div>
			</div>
		)
	}

	return (
		<div className={styles.layout}>
			<PageHeader
				title="Editar informações da demanda"
				onBack={() => history.push(`/demands/${id}`)}
				className={styles.header}
			/>
			<div className={styles.content}>
				{(isLoading || isLoadingAttachments) && <Spin />}
				{!isLoading && !isLoadingAttachments && (
					<StyledForm layout="vertical" onFinish={handleSubmit(onSubmit)}>
						<FormSection
							title="Informações da demanda"
							description="Preencha cuidadosamente as informações da demanda
													para que o responsável jurídico tenha um entendimento claro sobre
													a solicitação."
						/>
						<StyledFormItem className={styles.formItem}>
							<p className={styles.label}>Área Solicitante</p>
							<Controller
								name="solicitantAreaId"
								control={control}
								render={({ field }) => (
									<Select
										{...field}
										placeholder="Área solicitante"
										onChange={(value) => field.onChange(value)}
										disabled={true}
										loading={isLoadingAreas}>
										{getSelectOptions(solicitantAreas)}
									</Select>
								)}
							/>
						</StyledFormItem>
						<InputForm
							name="taskName"
							control={control}
							label={taskNameLabel}
							placeholder={taskNameLabel}
							required
							disabled={!isTaskNameEditable}
						/>
						<TextAreaForm
							name="description"
							control={control}
							label="Descrição"
							placeholder="Escreva os detalhes sobre a demanda"
							required
							disabled
						/>

						{/* Category Select */}
						<StyledFormItem className={styles.formItem} required>
							<p className={styles.label}>Categoria</p>
							<Controller
								name="categoryId"
								control={control}
								rules={{ required: 'Selecione uma categoria' }}
								render={({ field, fieldState }) => (
									<>
										<Select
											{...field}
											placeholder="Selecione uma categoria"
											onChange={(value) => field.onChange(value)}
											disabled={isLoadingCategories || requireTemplate}
											notFoundContent={
												isLoadingCategories ? <Spin size="small" /> : null
											}>
											{isLoadingCategories && (
												<Select.Option disabled>Carregando...</Select.Option>
											)}
											{getSelectOptions(categories)}
										</Select>

										{fieldState.error && (
											<span className={styles.errorMessage}>
												{fieldState.error.message}
											</span>
										)}
									</>
								)}
							/>
						</StyledFormItem>

						{/* Model Select */}
						{requireTemplate && (
							<StyledFormItem className={styles.formItem} required>
								<p className={styles.label}>Modelo</p>
								<Controller
									name="templateId"
									control={control}
									rules={{ required: 'Selecione um modelo' }}
									render={({ field, fieldState }) => (
										<>
											<Select
												{...field}
												placeholder="Selecione um modelo"
												onChange={(value) => field.onChange(value)}
												disabled={true}
												notFoundContent={
													isLoadingModels ? <Spin size="small" /> : null
												}>
												{isLoadingModels && (
													<Select.Option disabled>Carregando...</Select.Option>
												)}
												{getSelectOptions(models)}
											</Select>

											{fieldState.error && (
												<span className={styles.errorMessage}>
													{fieldState.error.message}
												</span>
											)}
										</>
									)}
								/>
							</StyledFormItem>
						)}

						{/* Priority Select */}
						<StyledFormItem className={styles.formItem} required>
							<p className={styles.label}>Prioridade</p>
							<Controller
								name="priority"
								control={control}
								rules={{ required: 'Selecione a prioridade' }}
								render={({ field, fieldState }) => (
									<>
										<Select
											{...field}
											placeholder="Selecione a prioridade"
											onChange={(value) => field.onChange(value)}>
											{getSelectOptions(priorities)}
										</Select>

										{fieldState.error && (
											<span className={styles.errorMessage}>
												{fieldState.error.message}
											</span>
										)}
									</>
								)}
							/>
						</StyledFormItem>

						{/* Status Select */}
						<StyledFormItem className={styles.formItem} required>
							<p className={styles.label}>Status</p>
							<Controller
								name="status"
								control={control}
								rules={{ required: 'Selecione o status' }}
								render={({ field, fieldState }) => (
									<>
										<Select
											{...field}
											placeholder="Selecione o status"
											onChange={(value) => field.onChange(value)}
											options={statusOptions}
										/>
										{fieldState.error && (
											<span className={styles.errorMessage}>
												{fieldState.error.message}
											</span>
										)}
									</>
								)}
							/>
						</StyledFormItem>

						{/* Attachments */}
						<StyledFormItem className={styles.formItem}>
							<p className={styles.label}>Anexos</p>
							<Uploader
								textButton="Adicionar anexo"
								allowedTypes={Object.keys(mimeTypes)}
								allowedExtensions={Object.values(mimeTypes)}
								multiple={true}
								max={5}
								fileList={fileList}
								setFileList={(files) => setValue('fileList', files)}
								removeDoc={handleRemoveAttachment}
							/>
							<p className={styles.additionalInfo}>
								* Limite total: <strong>125 MB</strong>
							</p>
						</StyledFormItem>

						<div className={styles.footer}>
							<Button
								loading={isLoadingEdit}
								disabled={isLoadingEdit}
								ghost={isLoadingEdit}
								type="primary"
								htmlType="submit">
								Salvar
							</Button>
						</div>
					</StyledForm>
				)}
			</div>
		</div>
	)
}

export default EditDemand
